// Operator console for one session. Either drives a separate patient display window
// (two screens) or runs the tests fullscreen in this window (one screen / home).

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type SessionInfo, type TestInfo } from "../api";
import { useAuth } from "../auth";
import { latestScored, resultText as plainResult } from "../lib/results";
import { Stage } from "../components/Stage";
import { errorText } from "../hooks";
import type { ResponseKey } from "../lib/protocol";
import { sessionChannel, type ConsoleToDisplay, type DisplayToConsole } from "../runner/channel";
import { COMPLIANCE_TEXT, EYE_INSTRUCTION, testTitle, useRunner } from "../runner/useRunner";

interface Progress { testId: number; seq: number; phase: string; value: number; correct: boolean; label: string;
  bg_code10?: number; letter_code10?: number; stroke_px?: number }

export function SessionConsole() {
  const id = Number(useParams().id);
  const nav = useNavigate();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [mode, setMode] = useState<"remote" | "local">("remote");
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => api.session(id).then(setSession, (e) => setError(errorText(e))), [id]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { if (session?.mode === "home") setMode("local"); }, [session?.mode]);

  if (!session) return <div>{error ?? "Loading…"}</div>;
  const next = session.tests.find((t) => t.status === "pending" || t.status === "running") ?? null;

  const finish = async () => {
    try {
      await api.completeSession(id);
      nav(`/sessions/${id}`);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <>
      <h1>Session {id}</h1>
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <div>Viewing distance <strong>{(session.viewing_distance_mm / 1000).toFixed(2)} m</strong> · calibration <span className={`badge ${session.calibration_tier}`}>{session.calibration_tier}</span>
              {session.qualification?.demo && <> · <span className="badge demo">debug: screen not checked</span></>}</div>
            <div className="small muted">Check the patient is at the marked distance, room lights as usual, distance correction on.</div>
          </div>
          {session.mode === "clinic" && (
            <label>Patient screen
              <select aria-label="Patient screen" value={mode} onChange={(e) => setMode(e.target.value as "remote" | "local")}>
                <option value="remote">Separate display window</option>
                <option value="local">This window, fullscreen</option>
              </select>
            </label>
          )}
        </div>
      </div>
      <LatestResult session={session} />
      <TestList tests={session.tests} canRepeat={session.status === "in_progress"} onChange={reload} />
      {mode === "remote"
        ? <RemoteControl session={session} next={next} onChange={reload} />
        : <LocalRun session={session} next={next} onChange={reload} />}
      {error && <div className="error">{error}</div>}
      <div className="row" style={{ marginTop: "1rem" }}>
        <button onClick={() => void finish()} className={next ? "secondary" : "big"}>
          {next ? "End session now" : session.mode === "home" ? "See my results" : "Finish session and show results"}
        </button>
        <Link to={`/patients/${session.patient_id}`}>Back to patient</Link>
      </div>
    </>
  );
}

/** The result of the test just finished, straight away (between acuity and contrast, say).
 *  Staff always see it; a patient sees it unless the program turns it off. */
function LatestResult({ session }: { session: SessionInfo }) {
  const { user } = useAuth();
  const last = latestScored(session.tests);
  const r = last ? plainResult(last) : null;
  if (!r) return null;
  if (user?.role === "patient" && session.protocol.show_each_result === false) return null;
  return (
    <div className={`card result-card ${r.counted ? "" : "bad-card"}`} role="status" aria-live="polite">
      <div className="small muted">Result just in</div>
      <h2 style={{ margin: "0.2rem 0" }}>{r.title}: {r.value ?? "not scored"}</h2>
      <p style={{ margin: 0 }}>{r.detail}</p>
      {r.notes.map((n) => <p key={n} className="small warn" style={{ margin: "0.3rem 0 0" }}>{n}</p>)}
    </div>
  );
}

export function needsRepeat(t: TestInfo, all: TestInfo[]): boolean {
  const failed = t.status === "aborted" || (!!t.result && t.result.valid === false);
  return failed && !all.some((o) => o.retry_of === t.id);
}

function TestList({ tests, canRepeat, onChange }: { tests: TestInfo[]; canRepeat: boolean; onChange: () => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="card">
      <table>
        <thead><tr><th>#</th><th>Test</th><th>Status</th><th>Result</th><th /></tr></thead>
        <tbody>
          {tests.map((t) => (
            <tr key={t.id}>
              <td>{Math.floor(t.order) + 1}{t.retry_of ? (t.purpose === "confirm" ? " (check)" : " (repeat)") : ""}</td>
              <td>{testTitle(t)}</td>
              <td>
                {t.status}
                {t.result && t.result.valid === false && (
                  <div className="bad small">not counted: {(t.result.invalid_reasons ?? []).join("; ")}</div>
                )}
              </td>
              <td>{resultText(t)}</td>
              <td>
                {canRepeat && needsRepeat(t, tests) && (
                  <button className="secondary" onClick={() => api.retryTest(t.id).then(onChange, (e) => setError(errorText(e)))}>Repeat</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function resultText(t: TestInfo): string {
  const r = t.result;
  if (!r) return "—";
  if (t.kind === "acuity") return typeof r.logmar === "number" ? `${r.logmar.toFixed(2)} logMAR` : "—";
  if (r.letter_logmar === null || r.letter_logmar === undefined) return "size gate not passed";
  const unit = r.measure === "log_modulation" ? "log modulation" : "log CS";
  return typeof r.log_cs === "number" ? `${r.log_cs.toFixed(2)} ${unit}` : `below 0.00 ${unit}`;
}

function ResponsePad({ onKey, disabled }: { onKey: (k: ResponseKey) => void; disabled?: boolean }) {
  return (
    <div className="pad">
      <button className="up" disabled={disabled} onClick={() => onKey("up")}>↑</button>
      <button className="left" disabled={disabled} onClick={() => onKey("left")}>←</button>
      <button className="none secondary" disabled={disabled} onClick={() => onKey("none")}>can't see</button>
      <button className="right" disabled={disabled} onClick={() => onKey("right")}>→</button>
      <button className="down" disabled={disabled} onClick={() => onKey("down")}>↓</button>
    </div>
  );
}

function RemoteControl({ session, next, onChange }: { session: SessionInfo; next: TestInfo | null; onChange: () => void }) {
  const chan = useRef<BroadcastChannel | null>(null);
  const [ready, setReady] = useState<{ fullscreen: boolean; screen: string; float16: boolean } | null>(null);
  const [running, setRunning] = useState<number | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [sinkState, setSinkState] = useState<{ pending: number; error: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const c = sessionChannel(session.id);
    chan.current = c;
    c.onmessage = (ev: MessageEvent<DisplayToConsole>) => {
      const m = ev.data;
      if (m.type === "ready") setReady(m);
      else if (m.type === "progress") setProgress(m);
      else if (m.type === "sink") setSinkState(m);
      else if (m.type === "test-done") { setRunning(null); setProgress(null); onChange(); }
      else if (m.type === "error") { setError(m.message); setRunning(null); onChange(); }
    };
    c.postMessage({ type: "hello" } satisfies ConsoleToDisplay);
    return () => c.close();
  }, [session.id, onChange]);

  const send = (m: ConsoleToDisplay) => chan.current?.postMessage(m);

  // While a test runs, the operator can also answer with the arrow keys / space here.
  useEffect(() => {
    if (running === null) return;
    const keys: Record<string, ResponseKey> = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", Space: "none" };
    const onKey = (e: KeyboardEvent) => {
      const k = keys[e.code];
      if (!k || e.repeat || (e.target as HTMLElement)?.tagName === "INPUT") return;
      e.preventDefault();
      chan.current?.postMessage({ type: "response", key: k, epochMs: performance.timeOrigin + e.timeStamp } satisfies ConsoleToDisplay);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running]);
  const open = () => {
    window.open(`/display/${session.id}`, `vtlite-display-${session.id}`, "popup,width=900,height=600");
    setTimeout(() => send({ type: "hello" }), 1500);
  };
  const respond = (k: ResponseKey) => send({ type: "response", key: k, epochMs: performance.timeOrigin + performance.now() });

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Patient display</h2>
      {!ready ? (
        <button onClick={open}>Open patient display window</button>
      ) : (
        <p>
          Display window connected{ready.fullscreen ? `, fullscreen on ${ready.screen}` : " — not fullscreen yet (click Go fullscreen in it)"}.
          {" "}Drawing buffer: {ready.float16 ? "float16 (10-bit capable)" : <span className="warn">8-bit</span>}.
        </p>
      )}
      {next && (
        <div className="row" style={{ alignItems: "center" }}>
          <div>
            <strong>Next: {testTitle(next)}</strong>
            <div className="small muted">{EYE_INSTRUCTION[next.eye]}</div>
          </div>
          <button disabled={!ready?.fullscreen || running !== null}
            onClick={() => { setError(null); setRunning(next.id); send({ type: "run", testId: next.id }); }}>
            Start test
          </button>
          {running !== null && <button className="secondary" onClick={() => send({ type: "abort" })}>Abort test</button>}
        </div>
      )}
      {running !== null && (
        <div className="row" style={{ marginTop: "1rem", alignItems: "center" }}>
          <ResponsePad onKey={respond} />
          <div className="small">
            {progress ? <>Trial {progress.seq + 1} ({progress.phase}{progress.label ? `, ${progress.label}` : ""}): value {progress.value.toFixed(2)}, last answer {progress.correct ? <span className="good">correct</span> : <span className="bad">wrong</span>}
              <div className="muted">debug: stroke {progress.stroke_px} px, background {progress.bg_code10}/1023, letter {progress.letter_code10}/1023</div></> : "Starting…"}
            {sinkState?.error && <div className="warn">Saving trials: {sinkState.error} ({sinkState.pending} waiting)</div>}
            <div className="muted">Enter what the patient reports (buttons or arrow keys here), or let them use the keypad on the display computer.</div>
          </div>
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function LocalRun({ session, next, onChange }: { session: SessionInfo; next: TestInfo | null; onChange: () => void }) {
  const r = useRunner(session);
  const [show, setShow] = useState(false);
  const [stopped, setStopped] = useState<string | null>(null);

  const start = async () => {
    if (!next) return;
    setShow(true);
    await new Promise((res) => setTimeout(res, 50));
    try {
      await r.stage.current?.enterFullscreen();
    } catch {
      /* fullscreen refused (e.g. no user gesture left): the stage still covers the window */
    }
    await new Promise((res) => setTimeout(res, 1500));
    setStopped(null);
    const done = await r.run(next, undefined, undefined,
      { requireFocus: true, requireFullscreen: session.mode === "home" });
    if (document.fullscreenElement) await document.exitFullscreen();
    setShow(false);
    if (done && (done.status === "aborted" || done.result?.valid === false)) {
      // a compliance stop: offer the repeat straight away
      setStopped((done.result?.invalid_reasons ?? []).join("; ") || "the test was stopped");
      try { await api.retryTest(done.id); } catch { /* already repeated */ }
    }
    onChange();
  };

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Run on this screen</h2>
      {r.stationError && <p className="error">{r.stationError}</p>}
      {r.geometryWarning && <p className="warn small">{r.geometryWarning}</p>}
      {next ? (
        <>
          {next.purpose === "confirm" && (
            <p className="warn">That result was quite different from your earlier ones, so this test is done once more to check.</p>
          )}
          <p><strong>Next: {testTitle(next)}.</strong> {EYE_INSTRUCTION[next.eye]}</p>
          <p className="small muted">Answer with the arrow keys: the direction the E's bars point. Press space if you cannot see it. Esc stops the test.</p>
          <button disabled={!r.station} onClick={() => void start()}>Start test</button>
        </>
      ) : <p>All tests are done.</p>}
      {r.lastError && <p className="error">{r.lastError}</p>}
      {stopped && (
        <p className="warn">
          That test did not count ({r.complianceReason ? COMPLIANCE_TEXT[r.complianceReason] ?? stopped : stopped}).
          It has been added again; press Start test when you are ready.
        </p>
      )}
      {show && (
        <Stage ref={r.stage}>
          <EscToAbort onAbort={r.abort} />
        </Stage>
      )}
    </div>
  );
}

function EscToAbort({ onAbort }: { onAbort: () => void }) {
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) onAbort(); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [onAbort]);
  return null;
}
